import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "@tanstack/react-form";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "@/components/common/required-input";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PAYMENT_METHOD_OPTIONS,
  SALE_LINE_ROW_GRID,
  SALE_PAYMENT_ROW_GRID,
} from "@/constants/sales/sales-constants";
import {
  EMPTY_SALE_PAYMENT_LINE,
  EMPTY_SALE_PRODUCT_LINE,
  getSaleCheckoutFormValues,
} from "@/default-form-values/sales/default-sale-form-values";
import { useGetProductsPaginated } from "@/hooks/products/use-products";
import { useCheckoutSale } from "@/hooks/sales/use-sales";
import { useGetStaffPaginated } from "@/hooks/staff/use-staff";
import {
  APPOINTMENT_TOAST_DURATION_MS,
  appointmentToastMessage,
} from "@/lib/appointments/appointment-toast";
import {
  staffLabel,
} from "@/lib/appointments/appointment-form-utils";
import { isoDate, isoTime } from "@/lib/utils";
import { getErrorMessage, isConflictError } from "@/lib/handle-mutation-error";
import {
  computeSaleFormTotal,
  paymentsMatchTotal,
  saleClientLabel,
  saleTimeSpan,
} from "@/lib/sales/sale-form-utils";
import { toSaleCheckout } from "@/mappers/sales/sale-form-to-request";
import type { Sale } from "@/types/sales/api-types/sale-types";
import { saleCheckoutSchema } from "@/zod-schemas/sales/sale-checkout-schema";
import { useIntl } from "react-intl";

interface SaleCheckoutDialogProps {
  sale: Sale;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SaleCheckoutDialog({
  sale,
  open,
  onOpenChange,
}: SaleCheckoutDialogProps) {
  const intl = useIntl();
  const checkoutSale = useCheckoutSale();
  const { data: staffResponse } = useGetStaffPaginated({
    active_only: true,
    size: 100,
  });
  const { data: productsResponse } = useGetProductsPaginated({ size: 100 });
  const staffList = staffResponse?.items ?? [];
  const products = productsResponse?.items ?? [];
  const clientName = saleClientLabel(sale.client_name);
  const span = saleTimeSpan(sale.services);
  const timeRange = span ? intl.formatDateTimeRange(isoTime(span.start), isoTime(span.end), { format: "clock" }) : null;

  const form = useForm({
    defaultValues: getSaleCheckoutFormValues(sale),
    validators: {
      onSubmit: saleCheckoutSchema,
    },
    onSubmit: async ({ value }) => {
      const validated = saleCheckoutSchema.parse(value);
      try {
        await checkoutSale.mutateAsync({
          id: sale.id,
          data: toSaleCheckout(validated),
        });
        toast.success(
          appointmentToastMessage({
            intl,
            action: "completed",
            variant: "success",
            clientName: sale.client_name ?? clientName,
            appointmentDate: sale.appointment_date,
          }),
          { duration: APPOINTMENT_TOAST_DURATION_MS }
        );
      } catch (error) {
        if (isConflictError(error)) {
          toast.error(
            getErrorMessage(error) ||
              "This sale has already been completed and is read-only"
          );
          return;
        }
        toast.error(
          appointmentToastMessage({
            intl,
            action: "completed",
            variant: "error",
            clientName: sale.client_name ?? clientName,
            appointmentDate: sale.appointment_date,
          }),
          { duration: APPOINTMENT_TOAST_DURATION_MS }
        );
        return;
      }
      form.reset();
      onOpenChange(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) form.reset();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Sale for {clientName}</DialogTitle>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{intl.formatDate(isoDate(sale.appointment_date), { format: "long" })}</span>
            {timeRange ? <span>{timeRange}</span> : null}
          </div>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="sale-checkout-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="services"
              mode="array"
              children={(linesField) => (
                <>
                  <p className="text-sm font-semibold">Services</p>
                  {linesField.state.value.map((_, index) => {
                    const booked = sale.services[index];
                    return (
                      <div key={index} className={SALE_LINE_ROW_GRID}>
                        <form.Field
                          name={`services[${index}].service_id`}
                          children={(field) => (
                            <Field>
                              <FieldLabel htmlFor={field.name}>
                                Service
                                <RequiredInput />
                              </FieldLabel>
                              <Select
                                value={
                                  field.state.value
                                    ? String(field.state.value)
                                    : undefined
                                }
                                disabled
                              >
                                <SelectTrigger id={field.name} disabled>
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {booked ? (
                                    <SelectItem
                                      value={String(booked.service_id)}
                                    >
                                      {booked.service_name ?? "Service"}
                                    </SelectItem>
                                  ) : null}
                                </SelectContent>
                              </Select>
                            </Field>
                          )}
                        />
                        <form.Field
                          name={`services[${index}].staff_id`}
                          children={(field) => (
                            <Field>
                              <FieldLabel htmlFor={field.name}>
                                Stylist
                                <RequiredInput />
                              </FieldLabel>
                              <Select
                                value={
                                  field.state.value
                                    ? String(field.state.value)
                                    : undefined
                                }
                                disabled
                              >
                                <SelectTrigger id={field.name} disabled>
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {booked ? (
                                    <SelectItem value={String(booked.staff_id)}>
                                      {booked.staff_name ?? "Stylist"}
                                    </SelectItem>
                                  ) : null}
                                </SelectContent>
                              </Select>
                            </Field>
                          )}
                        />
                        <form.Field
                          name={`services[${index}].price`}
                          children={(field) => {
                            const isInvalid =
                              field.state.meta.isTouched &&
                              !field.state.meta.isValid;
                            return (
                              <Field data-invalid={isInvalid}>
                                <FieldLabel htmlFor={field.name}>
                                  Price
                                  <RequiredInput />
                                </FieldLabel>
                                <div className="relative">
                                  <span
                                    aria-hidden="true"
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                                  >
                                    $
                                  </span>
                                  <Input
                                    id={field.name}
                                    name={field.name}
                                    type="number"
                                    step="0.01"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    aria-invalid={isInvalid}
                                    className="pl-6"
                                  />
                                </div>
                                {isInvalid && (
                                  <FieldError errors={field.state.meta.errors} />
                                )}
                              </Field>
                            );
                          }}
                        />
                        <div />
                      </div>
                    );
                  })}
                </>
              )}
            />

            <Separator />

            <form.Field
              name="products"
              mode="array"
              children={(linesField) => (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Products</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Add product"
                      onClick={() =>
                        linesField.pushValue({ ...EMPTY_SALE_PRODUCT_LINE })
                      }
                    >
                      <Plus />
                    </Button>
                  </div>
                  {linesField.state.value.map((_, index) => (
                    <form.Subscribe
                      key={index}
                      selector={(state) => state.values.products[index]}
                      children={(line) => {
                        if (!line) return null;
                        return (
                          <div className={SALE_LINE_ROW_GRID}>
                            <form.Field
                              name={`products[${index}].product_id`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Product
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      value={
                                        field.state.value
                                          ? String(field.state.value)
                                          : undefined
                                      }
                                      onValueChange={(value) => {
                                        const next = Number(value);
                                        field.handleChange(next);
                                        const product = products.find(
                                          (item) => item.id === next
                                        );
                                        if (product) {
                                          form.setFieldValue(
                                            `products[${index}].price`,
                                            Number(product.retail_price)
                                          );
                                        }
                                      }}
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {products.map((product) => (
                                          <SelectItem
                                            key={product.id}
                                            value={String(product.id)}
                                          >
                                            {product.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`products[${index}].staff_id`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Stylist
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      value={
                                        field.state.value
                                          ? String(field.state.value)
                                          : undefined
                                      }
                                      onValueChange={(value) =>
                                        field.handleChange(Number(value))
                                      }
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {staffList.map((member) => (
                                          <SelectItem
                                            key={member.id}
                                            value={String(member.id)}
                                          >
                                            {staffLabel(member)}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`products[${index}].price`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Price
                                      <RequiredInput />
                                    </FieldLabel>
                                    <div className="relative">
                                      <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                                      >
                                        $
                                      </span>
                                      <Input
                                        id={field.name}
                                        name={field.name}
                                        type="number"
                                        step="0.01"
                                        value={field.state.value}
                                        onBlur={field.handleBlur}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        aria-invalid={isInvalid}
                                        className="pl-6"
                                      />
                                    </div>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="mt-7"
                              aria-label="Remove product"
                              onClick={() => linesField.removeValue(index)}
                            >
                              <Trash2 />
                            </Button>
                          </div>
                        );
                      }}
                    />
                  ))}
                </>
              )}
            />

            <Separator />

            <div className="flex items-end justify-between gap-4">
              <form.Field
                name="discount_percent"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid} className="max-w-32">
                      <FieldLabel htmlFor={field.name}>Discount</FieldLabel>
                      <div className="relative">
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          step="0.01"
                          min={0}
                          max={100}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          aria-invalid={isInvalid}
                          className="pr-8"
                        />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                        >
                          %
                        </span>
                      </div>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />
              <form.Subscribe
                selector={(state) => ({
                  services: state.values.services,
                  products: state.values.products,
                  discount_percent: state.values.discount_percent,
                })}
                children={({ services, products, discount_percent }) => (
                  <p className="text-xl font-bold">
                    Total{" "}
                    {intl.formatNumber(computeSaleFormTotal(
                        services.map((line) => line.price),
                        products.map((line) => line.price),
                        discount_percent
                      ), { format: "money" })}
                  </p>
                )}
              />
            </div>

            <Separator />

            <form.Field
              name="payments"
              mode="array"
              children={(linesField) => (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Payment</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Add payment"
                      onClick={() =>
                        linesField.pushValue({ ...EMPTY_SALE_PAYMENT_LINE })
                      }
                    >
                      <Plus />
                    </Button>
                  </div>
                  {linesField.state.value.map((_, index) => (
                    <form.Subscribe
                      key={index}
                      selector={(state) => state.values.payments[index]}
                      children={(line) => {
                        if (!line) return null;
                        return (
                          <div className={SALE_PAYMENT_ROW_GRID}>
                            <form.Field
                              name={`payments[${index}].method`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Method
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      value={field.state.value || undefined}
                                      onValueChange={(value) =>
                                        field.handleChange(
                                          value as typeof field.state.value
                                        )
                                      }
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {PAYMENT_METHOD_OPTIONS.map((option) => (
                                          <SelectItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`payments[${index}].amount`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Amount
                                      <RequiredInput />
                                    </FieldLabel>
                                    <div className="relative">
                                      <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                                      >
                                        $
                                      </span>
                                      <Input
                                        id={field.name}
                                        name={field.name}
                                        type="number"
                                        step="0.01"
                                        value={field.state.value}
                                        onBlur={field.handleBlur}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        aria-invalid={isInvalid}
                                        className="pl-6"
                                      />
                                    </div>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            {linesField.state.value.length > 1 ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="mt-7"
                                aria-label="Remove payment"
                                onClick={() => linesField.removeValue(index)}
                              >
                                <Trash2 />
                              </Button>
                            ) : (
                              <div />
                            )}
                          </div>
                        );
                      }}
                    />
                  ))}
                  <form.Subscribe
                    selector={(state) =>
                      paymentsMatchTotal(
                        state.values.payments.map((line) => line.amount),
                        computeSaleFormTotal(
                          state.values.services.map((line) => line.price),
                          state.values.products.map((line) => line.price),
                          state.values.discount_percent
                        )
                      )
                    }
                    children={(matches) =>
                      matches ? null : (
                        <Field data-invalid>
                          <FieldError>
                            Payments must equal the total
                          </FieldError>
                        </Field>
                      )
                    }
                  />
                </>
              )}
            />
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <form.Subscribe
            selector={(state) =>
              paymentsMatchTotal(
                state.values.payments.map((line) => line.amount),
                computeSaleFormTotal(
                  state.values.services.map((line) => line.price),
                  state.values.products.map((line) => line.price),
                  state.values.discount_percent
                )
              )
            }
            children={(matches) => (
              <Button
                type="submit"
                form="sale-checkout-form"
                className="w-24"
                disabled={!matches || checkoutSale.isPending}
              >
                Complete
              </Button>
            )}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
